import { useState } from 'react'
import { PageHeader, Panel, DataTable, Chip, Button, Modal, Field, FormRow, Select, StatTile, Checkbox, ConfirmDialog, useToast, type Column } from '../ds/index'
import { RateBar } from '../components/Viz'
import { duration } from './Dashboard'
import { useApi } from '../lib/useApi'
import { post, patch } from '../lib/api'
import { title, ago } from '../lib/format'
import type { User } from '../lib/types'
import { LoadingBlock, ErrorBlock } from '../components/Loading'
import { roleLabel, roleSees } from '../components/ViewAs'
import { useSession } from '../lib/session'
import { MIN_PASSWORD } from '../lib/password'

export default function People() {
  const { data, error, reload } = useApi<{ users: User[] }>('/api/users')
  const [adding, setAdding] = useState(false)
  const [link, setLink] = useState<{ user: User | { name: string; email: string; id: string }; url: string; reset: boolean } | null>(null)
  const [confirmLink, setConfirmLink] = useState<User | null>(null)
  const toast = useToast()
  const { me } = useSession()
  const org = me?.org.name || 'Org'

  const toggle = async (u: User) => {
    try { await patch(`/api/users/${u.id}`, { active: !u.active }); toast(u.active ? `${u.name} can no longer sign in` : `${u.name} can sign in again`, 'pos'); reload() } catch (e) { toast((e as Error).message, 'neg') }
  }

  const makeLink = async (u: User) => {
    setConfirmLink(null)
    try { const r = await post<{ url: string }>(`/api/users/${u.id}/invite`, {}); setLink({ user: u, url: r.url, reset: u.has_password }); reload() } catch (e) { toast((e as Error).message, 'neg') }
  }
  const login = (r: User) => {
    if (!r.active) return <Chip>Off</Chip>
    if (r.last_seen_at) return <span className="nowrap">Active<div className="muted">{ago(r.last_seen_at)}</div></span>
    if (r.invite_open_since) return <Chip tone="warn" dot>Invited, not in yet</Chip>
    return <Chip tone="warn" dot>{r.has_password ? 'Never signed in' : 'No password yet'}</Chip>
  }

  const cols: Column<User>[] = [
    { key: 'name', header: 'Name', cell: (r) => <span className="avatar-cell"><span className="avatar avatar-sm" aria-hidden="true">{r.name.split(' ').map((x) => x[0]).join('').slice(0, 2)}</span><span><strong className="nowrap">{r.name}</strong><div className="muted nowrap">{r.email}</div></span></span>, sortBy: (r) => r.name },
    { key: 'role', header: 'Role', cell: (r) => <Chip tone={r.role === 'spec' ? 'accent' : undefined}>{r.role === 'spec' ? 'SPEC' : title(r.role)}</Chip>, sortBy: (r) => r.role },
    { key: 'book', header: 'Book', cell: (r) => r.role === 'rep' ? <span>{r.group_names.length ? r.group_names.join(', ') : <span className="muted">No groups yet</span>}<div className="muted nowrap">{r.groups} group{r.groups === 1 ? '' : 's'}, {r.subscribers} subscribers</div></span> : '', sortBy: (r) => r.groups },
    { key: 'alerts', header: 'Phone alerts', cell: (r) => r.role === 'rep' ? (r.alert_devices ? <Chip tone="pos" dot>{r.alert_devices === 1 ? 'On' : `On, ${r.alert_devices} devices`}</Chip> : <Chip tone="warn" dot>Off</Chip>) : '', sortBy: (r) => r.alert_devices },
    { key: 'open', header: 'Waiting', numeric: true, cell: (r) => r.role === 'rep' ? (r.open_leads ? <Chip tone="warn">{r.open_leads}</Chip> : <span className="muted">0</span>) : '', sortBy: (r) => r.open_leads },
    { key: 'rate', header: 'Replied, 30 days', cell: (r) => r.role === 'rep' ? <RateBar num={r.responded_30d} den={r.assigned_30d} label={r.assigned_30d ? `${r.responded_30d} of ${r.assigned_30d}` : undefined} /> : '', sortBy: (r) => r.assigned_30d ? r.responded_30d / r.assigned_30d : -1 },
    { key: 'speed', header: 'Typical reply', numeric: true, cell: (r) => r.role === 'rep' ? <span className="nowrap">{duration(r.median_response_s)}<div className="muted">last {r.last_reply_at ? ago(r.last_reply_at) : 'never'}</div></span> : '', sortBy: (r) => r.median_response_s ?? 1e12 },
    { key: 'login', header: 'Login', cell: login, sortBy: (r) => r.last_seen_at || '' },
    { key: 'active', header: '', cell: (r) => (
      <span className="nowrap">
        {r.active && <Button variant="ghost" onClick={() => (r.has_password ? setConfirmLink(r) : makeLink(r))}>{r.has_password ? 'Reset password' : 'Sign-in link'}</Button>}
        <Button variant="ghost" onClick={() => toggle(r)}>{r.active ? 'Turn off' : 'Turn on'}</Button>
      </span>
    ) },
  ]

  return (
    <>
      <PageHeader title="People" subtitle="Everyone who signs in, and what each login is for." actions={<Button onClick={() => setAdding(true)}>Add person</Button>} />
      {data && (() => {
        const reps = data.users.filter((u) => u.role === 'rep' && u.active)
        const assigned = reps.reduce((a, u) => a + u.assigned_30d, 0)
        const replied = reps.reduce((a, u) => a + u.responded_30d, 0)
        return (
          <div className="grid-tiles" style={{ marginBottom: 'var(--s-6)' }}>
            <StatTile focal label="Active reps" value={reps.length} meta={(() => { const n = data.users.filter((u) => u.role === 'admin' && u.active).length; return `${n} admin${n === 1 ? '' : 's'}` })()} />
            <StatTile label="Leads waiting" value={reps.reduce((a, u) => a + u.open_leads, 0)} meta="across every rep" />
            <StatTile label="Replied, 30 days" value={assigned ? `${Math.round((replied / assigned) * 100)}%` : 'none'} meta={`${replied} of ${assigned} leads`} progress={assigned ? (replied / assigned) * 100 : undefined} />
          </div>
        )
      })()}
      {data && (
        <div className="logins-legend" style={{ marginBottom: 'var(--s-6)' }}>
          {(['admin', 'rep', ...(me?.user.role === 'spec' ? ['spec'] as const : [])] as const).map((role) => {
            const n = data.users.filter((u) => u.role === role && u.active).length
            return (
              <div key={role} className="logins-card">
                <div className="logins-card-head"><Chip tone={role === 'spec' ? 'accent' : undefined}>{roleLabel(role, org)}</Chip><span className="muted">{n} login{n === 1 ? '' : 's'}</span></div>
                <p>{roleSees(role, org)}</p>
              </div>
            )
          })}
          <div className="logins-card">
            <div className="logins-card-head"><Chip>Subscribers</Chip><span className="muted">no login</span></div>
            <p>{roleSees('subscriber', org)}</p>
          </div>
        </div>
      )}
      {error && <ErrorBlock error={error} />}
      <Panel className="panel-flush">{!data ? <div style={{ padding: 'var(--s-6)' }}><LoadingBlock /></div> : <DataTable columns={cols} rows={data.users} rowKey={(r) => r.id} />}</Panel>
      {adding && <AddPerson onClose={() => setAdding(false)} onSaved={(user, url) => { setAdding(false); reload(); if (url) setLink({ user, url, reset: false }); else toast('Added', 'pos') }} />}
      {confirmLink && (
        <ConfirmDialog title={`Make a password reset link for ${confirmLink.name}?`} confirmLabel="Make link"
                       body={`${confirmLink.name} already has a password. Whoever opens this link can set a new one, so send it only to them. Their current password keeps working until the link is used.`}
                       onConfirm={() => makeLink(confirmLink)} onCancel={() => setConfirmLink(null)} />
      )}
      {link && <SignInLink user={link.user} url={link.url} reset={link.reset} onClose={() => setLink(null)} />}
    </>
  )
}

/** The link to text or email someone. Emailing makes a fresh link, so the one shown is always the live one. */
function SignInLink({ user, url, reset, onClose }: { user: { id: string; name: string; email: string }; url: string; reset: boolean; onClose: () => void }) {
  const [live, setLive] = useState(url)
  const [busy, setBusy] = useState(false)
  const toast = useToast()
  const copy = () => navigator.clipboard?.writeText(live).then(() => toast('Link copied', 'pos'), () => toast('Copy did not work. Select the link instead.', 'neg'))
  const emailIt = async () => {
    setBusy(true)
    try {
      const r = await post<{ url: string; emailed: boolean; email_error: string | null }>(`/api/users/${user.id}/invite`, { send_email: true })
      setLive(r.url)
      if (r.emailed) toast(`Emailed to ${user.email}`, 'pos'); else toast(r.email_error || 'Not emailed', 'warn')
    } catch (e) { toast((e as Error).message, 'neg') } finally { setBusy(false) }
  }
  return (
    <Modal title={reset ? `Password reset link for ${user.name}` : `Sign-in link for ${user.name}`} onClose={onClose} footer={<Button onClick={onClose}>Done</Button>}>
      <div className="stack-tight">
        <p className="mailbox-note" style={{ margin: 0 }}>Text or email this to {user.name.split(' ')[0]}. {reset ? 'It opens a page to choose a new password and sign in. Their current password keeps working until it is used. ' : 'It opens a page to choose a password and sign straight in. '}It works once, for 7 days, and making another link voids this one.</p>
        <code className="comms-url invite-url">{live}</code>
        <div className="row-tight">
          <Button onClick={copy}>Copy link</Button>
          <Button variant="secondary" onClick={emailIt} disabled={busy}>{busy ? 'Emailing' : `Email it to ${user.email}`}</Button>
        </div>
      </div>
    </Modal>
  )
}

function AddPerson({ onClose, onSaved }: { onClose: () => void; onSaved: (user: { id: string; name: string; email: string }, inviteUrl: string | null) => void }) {
  const [f, setF] = useState({ name: '', email: '', role: 'rep', phone: '', password: '' })
  const [setPassword, setSetPassword] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const save = async () => {
    try {
      const r = await post<{ user: { id: string; name: string; email: string }; invite: { url: string } | null }>('/api/users', { ...f, password: setPassword ? f.password : undefined })
      onSaved(r.user, r.invite?.url || null)
    } catch (e) { setErr((e as Error).message) }
  }
  return (
    <Modal title="Add person" onClose={onClose} footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={!f.name || !f.email || (setPassword && f.password.length < MIN_PASSWORD)}>{setPassword ? 'Add' : 'Add and get sign-in link'}</Button></>}>
      <div className="stack-tight">
        <Field label="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        <Field label="Email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        <FormRow label="Role"><Select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })}><option value="rep">Rep</option><option value="admin">Admin</option></Select></FormRow>
        <Field label="Phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
        <Checkbox label="Set a starting password myself instead of sending a link" checked={setPassword} onChange={(e) => setSetPassword(e.target.checked)} />
        {setPassword && <FormRow label="Starting password" help={`At least ${MIN_PASSWORD} characters. Share it with them directly. They choose their own the first time they sign in.`}><input className="field" type="text" autoComplete="new-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} /></FormRow>}
        {!setPassword && <p className="form-help" style={{ margin: 0 }}>You get a link to text or email them. They choose their own password.</p>}
        {err && <p className="form-error">{err}</p>}
      </div>
    </Modal>
  )
}
